import { FC, memo, useCallback, useMemo } from 'react'

import { useTranslation, useLocale } from '../../../hooks/locale'
import { useDateFormatter } from '../../../hooks/date'
import { useSearchUrl } from '../../../hooks/search'
import { useRegionPriceAndAvailability } from '../../../hooks/availability'
import { getFilterFromHref } from '../../../utils/request-utils'
import { getTodayIsoDate } from '../../../utils/date-utils'
import AnalyticsService from '../../../services/analytics-service'
import { landingPageEventCategories } from '../../../services/analytics-service/categories'
import WeekChart, { ChartWeek, ExploreSource } from './week-chart'
import { Container, InnerContainer, Title } from './styles'

const DISPLAY_WEEKS = 53
const SEARCH_ADULTS = 2

type ExploreChart = 'availability' | 'price'

interface PriceAndAvailabilitySectionProps {
    locationName: string
}

const PriceAndAvailabilitySection: FC<PriceAndAvailabilitySectionProps> = ({ locationName }) => {
    const { t } = useTranslation()
    const { format } = useDateFormatter()
    const { getSearchUrl, isLoading } = useSearchUrl()
    const { data } = useRegionPriceAndAvailability()
    const { locale } = useLocale()

    const currencyFormatter = useMemo(
        () =>
            new Intl.NumberFormat(locale, {
                style: 'currency',
                currency: 'EUR',
                maximumFractionDigits: 0,
            }),
        [locale],
    )

    const { availabilityWeeks, priceWeeks, maxPercent, maxPrice, priceHighlight } = useMemo(() => {
        const today = getTodayIsoDate()
        const upcoming = (data?.weeks ?? []).filter(week => week.from > today).slice(0, DISPLAY_WEEKS)

        let percentMax = 1
        let priceMax = 1
        let firstPricedIndex = -1
        let firstPricedPrice: number | null = null
        upcoming.forEach((week, index) => {
            percentMax = Math.max(percentMax, week.availablePercent)
            priceMax = Math.max(priceMax, week.avgPricePerNight ?? 0)
            if (firstPricedPrice === null && week.avgPricePerNight !== null) {
                firstPricedIndex = index
                firstPricedPrice = week.avgPricePerNight
            }
        })

        const { availabilityWeeks, priceWeeks } = upcoming.reduce<{
            availabilityWeeks: ChartWeek[]
            priceWeeks: ChartWeek[]
        }>(
            (acc, week, index) => {
                const label = `${format(new Date(week.from), 'd MMM')} – ${format(new Date(week.to), 'd MMM')}`
                const base = {
                    from: week.from,
                    to: week.to,
                    label,
                    monthLabel: format(new Date(week.from), 'MMM'),
                    showMonthLabel: index > 0 && week.from.slice(0, 7) !== upcoming[index - 1].from.slice(0, 7),
                    explorable: week.availablePercent > 0,
                }

                acc.availabilityWeeks.push({
                    ...base,
                    barHeight: Math.max((week.availablePercent / percentMax) * 100, 1),
                    valueText: `${week.availablePercent}%`,
                    tooltipText: `${t('priceAndAvailability.tooltipLabel')} ${label}`,
                })

                acc.priceWeeks.push({
                    ...base,
                    barHeight:
                        week.avgPricePerNight === null ? 1 : Math.max((week.avgPricePerNight / priceMax) * 100, 1),
                    valueText: week.avgPricePerNight === null ? null : currencyFormatter.format(week.avgPricePerNight),
                    valueSuffix: t('priceAndAvailability.perNight'),
                    tooltipText:
                        week.avgPricePerNight === null
                            ? label
                            : `${t('priceAndAvailability.priceTooltipLabel')} ${label}`,
                })

                return acc
            },
            { availabilityWeeks: [], priceWeeks: [] },
        )

        return {
            maxPercent: percentMax,
            maxPrice: priceMax,
            availabilityWeeks,
            priceWeeks,
            priceHighlight:
                firstPricedPrice === null
                    ? null
                    : {
                          value: currencyFormatter.format(firstPricedPrice),
                          label: `${t('priceAndAvailability.for')} ${priceWeeks[firstPricedIndex].label}`,
                      },
        }
    }, [data, format, t, currencyFormatter])

    const handleExplore = useCallback(
        async (week: ChartWeek, source: ExploreSource, chart: ExploreChart) => {
            if (!week.explorable) {
                return
            }
            try {
                const searchUrl = await getSearchUrl(getFilterFromHref(globalThis.location.href), {
                    regionId: null,
                    search: locationName,
                    arrival: week.from,
                    departure: week.to,
                    adults: SEARCH_ADULTS,
                    children: 0,
                })

                if (searchUrl) {
                    AnalyticsService.trackEvent({
                        action: `${chart}_${source}`,
                        category: landingPageEventCategories.priceAndAvailabilitySection.SECTION,
                        label: searchUrl,
                    })
                    globalThis.location.href = `${globalThis.location.origin}${searchUrl}`
                }
            } catch {}
        },
        [getSearchUrl, locationName],
    )

    const handleExploreAvailability = useCallback(
        (week: ChartWeek, source: ExploreSource) => handleExplore(week, source, 'availability'),
        [handleExplore],
    )

    const handleExplorePrice = useCallback(
        (week: ChartWeek, source: ExploreSource) => handleExplore(week, source, 'price'),
        [handleExplore],
    )

    if (availabilityWeeks.length === 0) {
        return null
    }

    const annualAverage = data?.annualAveragePercent ?? null
    const annualPrice = data?.annualAvgPricePerNight ?? null
    const noValueText = t('priceAndAvailability.noAvailable')

    return (
        <Container id="price-and-availability">
            <InnerContainer>
                <Title>{t('priceAndAvailability.sectionTitle')}</Title>
                {!!priceHighlight && (
                    <WeekChart
                        title={`${t('priceAndAvailability.priceTitle')} ${locationName}`}
                        highlightValue={priceHighlight.value}
                        highlightLabel={priceHighlight.label}
                        averageLabel={
                            annualPrice === null
                                ? null
                                : `${currencyFormatter.format(annualPrice)} ${t('priceAndAvailability.annualAverage')}`
                        }
                        averageLineBottom={annualPrice === null ? null : Math.min((annualPrice / maxPrice) * 100, 100)}
                        axisLabel={t('priceAndAvailability.priceAxisLabel')}
                        noValueText={noValueText}
                        weeks={priceWeeks}
                        isLoading={isLoading}
                        onExplore={handleExplorePrice}
                    />
                )}
                <WeekChart
                    title={`${t('priceAndAvailability.title')} ${locationName}`}
                    highlightValue={availabilityWeeks[0].valueText ?? ''}
                    highlightLabel={`${t('priceAndAvailability.for')} ${availabilityWeeks[0].label}`}
                    averageLabel={
                        annualAverage === null ? null : `${annualAverage}% ${t('priceAndAvailability.annualAverage')}`
                    }
                    averageLineBottom={
                        annualAverage === null ? null : Math.min((annualAverage / maxPercent) * 100, 100)
                    }
                    axisLabel={t('priceAndAvailability.axisLabel')}
                    noValueText={noValueText}
                    weeks={availabilityWeeks}
                    isLoading={isLoading}
                    onExplore={handleExploreAvailability}
                />
            </InnerContainer>
        </Container>
    )
}

export default memo(PriceAndAvailabilitySection)
